import * as React from "react";
import { useState } from "react";
import {
  END_DATE,
  type FirstDayOfWeek,
  type FocusedInput,
  type OnDatesChangeProps,
  START_DATE,
  useDatepicker,
} from "@datepicker-react/hooks";

import { getLang, useI18nContext } from "@hopper-b2b/i18n";
import type { IMonthBucket } from "@hopper-b2b/types";
import { dayCountBelowMax, isBetweenDays } from "@hopper-b2b/utilities";
import { ColumnView, GroupView, HorizontalView } from "@tenant/ui";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import * as textConstant from "./constants";
import { context as DateRangePickerContext } from "./context";

import { PickerType } from "./types";

dayjs.extend(isBetween);

export interface IDateRangePickerProps {
  pickerType?: PickerType;
  startDate: Date | null;
  endDate: Date | null;
  months: IMonthBucket[] | null;
  columnView?: boolean;
  // focusedMonthIndex must be given in order to make columnView focus on an arbitrary month
  // note that this was not previously handled in the old design since it was always showing 2 months
  focusedMonthIndex?: number;
  // setStartDate and setEndDate must be given for rendering the horizontal/columnView
  setStartDate?: (date: Date | null) => void;
  setEndDate?: (date: Date | null) => void;
  minAllowedDate?: Date;
  maxAllowedDate?: Date;
  minNumDays?: number;
  maxNumDays?: number;
  groupView?: boolean;
  // setFocusedMonthIndex must be given for working with the groupView + columnView combination
  setFocusedMonthIndex?: (index: number) => void;
  // for coloured price tags
  priceTagsElement: JSX.Element;
  priceTags: string[];
  currency: string;
  startDateLabel?: string;
  endDateLabel?: string;
  className?: string;
  assets?: Record<string, any>;
  isMissingDate?: boolean;
  showIcons?: boolean;
  preventSameDateSelection?: boolean;
  /**
   * When set, dates returning true are not selectable (combined with min/max rules).
   * In-range dates blocked only by this predicate show a struck-through day label (out-of-range days keep the default disabled look).
   *
   * Omit this prop for default calendar behavior (same as before this API existed). As of introduction, only the activities shop
   * date picker passes it; other funnels should keep omitting it so they are unaffected.
   */
  isDateDisabled?: (date: Date) => boolean;
  a11yModeEnabled?: boolean;
}
export interface IDateRangePickerState {
  focusedInput: FocusedInput;
}

const initialState: IDateRangePickerState = {
  focusedInput: START_DATE,
};

const dayCountAboveMin = (
  date: Date,
  startDate: Date | null,
  endDate: Date | null,
  minNumDays?: number
) => {
  if (!minNumDays) {
    return true;
  }

  if (startDate !== null && endDate !== null) {
    return true;
  }

  // If only one of startDate or endDate is set, we don't really know if it's really a "start" or an "end".
  // We will allow dates in both past and future directions from such date.

  if (startDate) {
    return !dayjs(date).isBetween(
      dayjs(startDate).subtract(minNumDays - 1, "day"),
      dayjs(startDate).add(minNumDays - 1, "day"),
      "day",
      "[]"
    );
  }

  if (endDate) {
    return !dayjs(date).isBetween(
      dayjs(endDate).subtract(minNumDays - 1, "day"),
      dayjs(endDate).add(minNumDays - 1, "day"),
      "day",
      "[]"
    );
  }

  return true;
};

const dayCountWithinRange = (
  date: Date,
  startDate: Date | null,
  endDate: Date | null,
  minNumDays?: number,
  maxNumDays?: number
) => {
  return (
    dayCountAboveMin(date, startDate, endDate, minNumDays) &&
    dayCountBelowMax(date, startDate, endDate, maxNumDays)
  );
};

const isDateAllowed = (
  date: Date,
  startDate: Date | null,
  endDate: Date | null,
  minAllowedDate?: Date,
  maxAllowedDate?: Date,
  minNumDays?: number,
  maxNumDays?: number
) => {
  return (
    isBetweenDays(date, minAllowedDate, maxAllowedDate) &&
    dayCountWithinRange(date, startDate, endDate, minNumDays, maxNumDays)
  );
};

const generateOnDateSelect =
  ({
    startDate,
    endDate,
    handleDateChange,
    pickerType,
    preventSameDateSelection,
  }: {
    startDate: Date | null;
    endDate: Date | null;
    handleDateChange: (args: OnDatesChangeProps) => void;
    pickerType: PickerType;
    preventSameDateSelection: boolean;
  }) =>
  (date: Date) => {
    if (pickerType === PickerType.RANGE && startDate) {
      if (dayjs(date).isSameOrAfter(startDate, "day") && !endDate) {
        const isSameDay = dayjs(date).isSame(startDate, "day");
        if (!(preventSameDateSelection && isSameDay)) {
          handleDateChange({
            startDate: startDate,
            // If it's the same day, we set the end date as the start date so the time remains the same by default
            endDate: isSameDay ? startDate : date,
            focusedInput: END_DATE,
          });
        }
      } else {
        handleDateChange({
          startDate: date,
          endDate: null,
          focusedInput: START_DATE,
        });
      }
    } else {
      handleDateChange({
        startDate: date,
        endDate: null,
        focusedInput: START_DATE,
      });
    }
  };

const generateIsDateHovered =
  ({
    startDate,
    endDate,
    hoveredDate,
    pickerType,
    isDateAllowed,
    minAllowedDate,
    maxAllowedDate,
    minNumDays,
    maxNumDays,
  }: {
    startDate: Date | null;
    endDate: Date | null;
    hoveredDate: Date | null;
    pickerType: PickerType;
    isDateAllowed: (
      date: Date,
      startDate: Date | null,
      endDate: Date | null,
      minAllowedDate?: Date,
      maxAllowedDate?: Date,
      minNumDays?: number,
      maxNumDays?: number
    ) => boolean;
    minAllowedDate?: Date;
    maxAllowedDate?: Date;
    minNumDays?: number;
    maxNumDays?: number;
  }) =>
  (date: Date) => {
    if (
      pickerType === PickerType.RANGE &&
      hoveredDate &&
      startDate &&
      !endDate &&
      (startDate <= hoveredDate
        ? dayjs(date).isBetween(startDate, hoveredDate, "day", "[]")
        : dayjs(date).isBetween(hoveredDate, startDate, "day", "[]"))
    ) {
      if (
        isDateAllowed(
          date,
          startDate,
          endDate,
          minAllowedDate,
          maxAllowedDate,
          minNumDays,
          maxNumDays
        )
      ) {
        return true;
      }
      if (
        isDateAllowed(
          date,
          startDate,
          endDate,
          minAllowedDate,
          maxAllowedDate,
          minNumDays,
          maxNumDays
        ) &&
        isDateAllowed(
          hoveredDate,
          startDate,
          endDate,
          minAllowedDate,
          maxAllowedDate,
          minNumDays,
          maxNumDays
        )
      ) {
        return true;
      }
    }

    return (
      pickerType === PickerType.DAY &&
      !!hoveredDate &&
      isDateAllowed(
        hoveredDate,
        startDate,
        endDate,
        minAllowedDate,
        maxAllowedDate,
        minNumDays,
        maxNumDays
      ) &&
      dayjs(date).isSame(hoveredDate, "day")
    );
  };

export const firstWeekDay = (lang: string): number => {
  try {
    const intLoc = new Intl.Locale(lang);

    // @ts-ignore Remove once weekInfo is fully supported
    return intLoc?.weekInfo.firstDay || 0;
  } catch (e) {
    return 0;
  }
};

export const DateRangePicker = ({
  pickerType = PickerType.RANGE,
  startDate,
  endDate,
  columnView,
  focusedMonthIndex,
  setStartDate,
  setEndDate,
  months = [],
  minAllowedDate,
  maxAllowedDate,
  minNumDays,
  maxNumDays,
  groupView,
  setFocusedMonthIndex,
  priceTags,
  startDateLabel,
  endDateLabel,
  className,
  assets,
  showIcons = true,
  isMissingDate = false,
  priceTagsElement,
  preventSameDateSelection,
  isDateDisabled,
  a11yModeEnabled = false,
}: IDateRangePickerProps) => {
  const [state, setState] = useState(initialState);
  const { focusedInput } = state;
  const [ariaLiveMessage, setAriaLiveMessage] = React.useState("");
  const { formatDateTime, DateTimeFormatStyle } = useI18nContext();

  // If endDate is before startDate, we remove it to ensure this component does not throw.
  const cleanedEndDate =
    endDate && startDate && endDate < startDate ? null : endDate;

  const createAriaLiveMessage = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    const startDateMessage = startDate
      ? formatDateTime(startDate, DateTimeFormatStyle.LongDate)
      : "";
    const endDateMessage = endDate
      ? formatDateTime(endDate, DateTimeFormatStyle.LongDate)
      : "";
    if (startDateMessage)
      setAriaLiveMessage(
        textConstant.SELECTED_DATE(startDateMessage, endDateMessage)
      );
  };

  const handleDateChange = ({
    startDate,
    endDate,
    focusedInput,
  }: OnDatesChangeProps) => {
    if (!focusedInput) {
      setState({ focusedInput: START_DATE });
    } else {
      setState({ focusedInput });
    }
    if (startDate) createAriaLiveMessage(startDate, endDate);
    if (setStartDate && focusedInput === "startDate") {
      setStartDate(startDate);
    }
    if (setEndDate && focusedInput === "endDate") {
      setEndDate(endDate);
    } else if (setEndDate) {
      setEndDate(null);
    }
  };

  const numberOfMonths = groupView || columnView ? 12 : 2;
  const firstDayOfWeek = firstWeekDay(getLang()) as FirstDayOfWeek; // 0 is Sunday.

  const {
    isDateSelected,
    isFirstOrLastSelectedDate,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateFocus,
    onResetDates,
    hoveredDate,
    goToPreviousMonths,
    goToNextMonths,
  } = useDatepicker({
    firstDayOfWeek: firstDayOfWeek,
    startDate,
    endDate: cleanedEndDate,
    focusedInput,
    onDatesChange: handleDateChange,
    numberOfMonths,
  });

  const isDateHovered = generateIsDateHovered({
    startDate,
    endDate,
    hoveredDate,
    pickerType,
    isDateAllowed,
    minAllowedDate,
    maxAllowedDate,
    minNumDays,
    maxNumDays,
  });

  const onDateSelect = generateOnDateSelect({
    startDate,
    endDate,
    handleDateChange,
    pickerType,
    preventSameDateSelection: preventSameDateSelection ?? true,
  });

  // Check if a date is blocked - either by date range constraints or custom disable logic
  // Note: Uses short-circuit evaluation - if !isDateAllowed is true,
  // isDateDisabled won't be called (optimization for performance)
  const isDateBlockedFunction = (date: Date) =>
    !isDateAllowed(
      date,
      startDate,
      endDate,
      minAllowedDate,
      maxAllowedDate,
      minNumDays,
      maxNumDays
    ) ||
    (isDateDisabled?.(date) ?? false);

  const shouldShowUnavailableStrikethrough =
    isDateDisabled != null
      ? (date: Date) =>
          isDateDisabled(date) &&
          isDateAllowed(
            date,
            startDate,
            endDate,
            minAllowedDate,
            maxAllowedDate,
            minNumDays,
            maxNumDays
          )
      : () => false;

  return (
    <DateRangePickerContext.Provider
      value={{
        focusedDate,
        hoveredDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked: isDateBlockedFunction,
        shouldShowUnavailableStrikethrough,
        isFirstOrLastSelectedDate,
        onDateSelect,
        onDateFocus,
        onDateHover,
        onResetDates,
        goToPreviousMonths,
        goToNextMonths,
        startDate,
        endDate,
        focusedInput,
        pickerType,
        months,
      }}
    >
      {columnView && !groupView ? (
        <ColumnView
          className={className}
          startDate={startDate}
          cleanedEndDate={cleanedEndDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          minAllowedDate={minAllowedDate ?? null}
          maxAllowedDate={maxAllowedDate ?? null}
          numberOfMonths={numberOfMonths}
          focusedMonthIndex={focusedMonthIndex}
          pickerType={pickerType}
          startDateLabel={startDateLabel}
          endDateLabel={endDateLabel}
          assets={assets}
          isMissingDate={isMissingDate}
          showIcons={showIcons}
          priceTags={priceTags}
          priceTagsElement={priceTagsElement}
          a11yModeEnabled={a11yModeEnabled}
        />
      ) : !columnView && groupView ? (
        <GroupView
          minAllowedDate={minAllowedDate ?? null}
          maxAllowedDate={maxAllowedDate ?? null}
          setFocusedMonthIndex={setFocusedMonthIndex}
          numberOfMonths={numberOfMonths}
          priceTagsElement={priceTagsElement}
        />
      ) : (
        <HorizontalView
          className={className}
          startDate={startDate}
          cleanedEndDate={cleanedEndDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          minAllowedDate={minAllowedDate ?? null}
          maxAllowedDate={maxAllowedDate ?? null}
          numberOfMonths={numberOfMonths}
          priceTagsElement={<></>}
          priceTags={priceTags}
          a11yModeEnabled={a11yModeEnabled}
        />
      )}
      <div aria-live="polite" className="sr-only">
        {ariaLiveMessage}
      </div>
    </DateRangePickerContext.Provider>
  );
};
